/* eslint-disable import/prefer-default-export */
import { Auth, Users, Constants } from '@leverege/api-redux';
import { GlobalState } from '@leverege/ui-redux';
import { Plugins } from '@leverege/plugin';
import { push } from 'connected-react-router';
import { jwtDecode } from 'jwt-decode';
import { TWO_FACTOR_TYPES } from './Constants.js';
export function getTwoFactorMethodsForProject(projectSettings) {
  const filter = plg => {
    if (!projectSettings || !projectSettings.supportedProtocols) {
      return true;
    }
    return projectSettings.supportedProtocols[plg.type];
  };
  return Plugins.createView('TwoFactorAuthMethod', {
    filter
  }).get();
}
export function getTwoFactorMethodsForUser(userProfile) {
  const filter = plg => {
    if (!userProfile || !userProfile.twoFactorMethodsConfigured) {
      return plg.isUniversal;
    }
    return userProfile.twoFactorMethodsConfigured[plg.type] || plg.isUniversal;
  };
  return Plugins.createView('TwoFactorAuthMethod', {
    filter,
    name: userProfile?.id
  }).get();
}
export function defaultAdjustPath(path) {
  return path;
}
export function createOnActivateAccount(opts) {
  if (opts?.activateAccount?.onActivateAccount) {
    return opts.activateAccount.onActivateAccount;
  }
  return data => {
    return GlobalState.dispatch(Auth.activateAccount(data));
  };
}
export function createOnLogin(opts) {
  if (opts?.login?.onLogin) {
    return opts.login.onLogin;
  }
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const twoFactorVerifyPath = adjustPath(opts?.twoFactorVerify?.path || '/twoFactorVerify');
  const twoFactorEnrollPath = adjustPath(opts?.twoFactorEnroll?.path || '/twoFactorEnroll');
  const postLoginUrl = adjustPath(opts?.postLoginUrl || '/');
  return (username, password) => {
    const action = !Auth.isLoggedIn(GlobalState.get().auth) ? Auth.login(username, password) : Auth.renew();
    return GlobalState.dispatch(action).then(res => {
      if (res.code === 'imagine.login.deviceCodeRequestSuccessful') {
        GlobalState.dispatch(push(postLoginUrl));
        return res;
      }
      const decoded = jwtDecode(res.token);
      switch (decoded.type) {
        case TWO_FACTOR_TYPES.VERIFY:
          {
            const methods = Object.keys(res.twoFactorMethodsConfigured);
            if (methods.length === 1) {
              GlobalState.dispatch(push(`${twoFactorVerifyPath}?m=${methods[0]}`));
            } else {
              GlobalState.dispatch(push(`${twoFactorVerifyPath}`));
            }
            break;
          }
        case TWO_FACTOR_TYPES.REQUIRE_ENROLL:
          {
            GlobalState.dispatch(push(twoFactorEnrollPath));
            break;
          }
        default:
          GlobalState.dispatch(push(postLoginUrl));
      }
      return res;
    });
  };
}
export function createOnLoginWith2FA(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const postLoginUrl = adjustPath(opts?.postLoginUrl || '/');
  return (code, method, rememberMe) => {
    return GlobalState.dispatch(Auth.loginWith2FA(code, method.type, rememberMe)).then(res => {
      if (res.code === 'imagine.login.deviceCodeRequestSuccessful') {
        GlobalState.dispatch(push('/login/device/success'));
      } else {
        GlobalState.dispatch(push(postLoginUrl));
      }
      return res;
    });
  };
}
export function createOnRequestLoginCode(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  return (username, method) => {
    const loginByCodePath = adjustPath(opts?.loginByCode?.path || '/loginByCode');
    return GlobalState.dispatch(Auth.requestLoginCode(username, method)).then(() => {
      return GlobalState.dispatch(push(`${loginByCodePath}?u=${encodeURIComponent(username)}`));
    });
  };
}
export function createOnRequestDeviceAccessToken(opts) {
  const {
    clientId,
    adjustPath
  } = opts;
  const postLoginUrl = adjustPath(opts?.postLoginUrl || '/');
  return projectId => {
    return GlobalState.dispatch(Auth.requestDeviceAccessToken(clientId, projectId || window.Molten?.getConfig?.('api/projectId') || process.env.PROJECT_ID)).then(() => {
      GlobalState.dispatch(push(postLoginUrl));
    }).catch(err => {
      if (err.code !== 'authorization_pending') {
        console.error(err);
      }
    });
  };
}
export function createOnRequestDeviceCode(opts) {
  const {
    clientId
  } = opts;
  return projectId => {
    return GlobalState.dispatch(Auth.requestDeviceCode(clientId, projectId || window.Molten?.getConfig?.('api/projectId') || process.env.PROJECT_ID));
  };
}
export function createOnConfirmUserCode(opts) {
  const {
    clientId: dClientId
  } = opts || {};
  return (userCode, clientId = dClientId, projectId = window.Molten?.getConfig?.('api/projectId') || process.env.PROJECT_ID) => {
    return GlobalState.dispatch(Auth.confirmUserCode(userCode, clientId, projectId));
  };
}
export function createOnLoginByCode(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const twoFactorVerifyPath = adjustPath(opts?.twoFactorVerify?.path || '/twoFactorVerify');
  const twoFactorEnrollPath = adjustPath(opts?.twoFactorEnroll?.path || '/twoFactorEnroll');
  const postLoginUrl = adjustPath(opts?.postLoginUrl || '/');
  return (username, code) => {
    return GlobalState.dispatch(Auth.loginWithCode(username, code)).then(res => {
      if (res.code === 'imagine.login.deviceCodeRequestSuccessful') {
        GlobalState.dispatch(push('/login/device/success'));
        return res;
      }
      const decoded = jwtDecode(res.token);
      switch (decoded.type) {
        case TWO_FACTOR_TYPES.VERIFY:
          {
            const methods = Object.keys(res.twoFactorMethodsConfigured);
            if (methods.length === 1) {
              GlobalState.dispatch(push(`${twoFactorVerifyPath}?m=${methods[0]}`));
            } else {
              GlobalState.dispatch(push(`${twoFactorVerifyPath}`));
            }
            break;
          }
        case TWO_FACTOR_TYPES.REQUIRE_ENROLL:
          {
            GlobalState.dispatch(push(twoFactorEnrollPath));
            break;
          }
        default:
          GlobalState.dispatch(push(postLoginUrl));
      }
      return res;
    });
  };
}
export function defaultOnIdentityProviderClick({
  data: loginUrl
}) {
  window.location = loginUrl;
}
export function createOnLoginByCodeClick(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const requestLoginCodePath = adjustPath(opts?.requestLoginCode?.path || '/requestLoginCode');
  return () => {
    return GlobalState.dispatch(push(`${requestLoginCodePath}`));
  };
}
export function createOnRequestDeviceCodeClick(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const requestDeviceCodePath = adjustPath(opts?.requestDeviceCode?.path || '/requestDeviceCode');
  return () => {
    return GlobalState.dispatch(push(`${requestDeviceCodePath}`));
  };
}
export function createOnForgotPasswordClick(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const forgotPasswordPath = adjustPath(opts?.forgotPassword?.path || '/forgotPassword');
  return () => {
    return GlobalState.dispatch(push(forgotPasswordPath));
  };
}
export function createOnBackToLogin(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const loginPath = adjustPath(opts?.login?.path);
  const deviceLoginPath = adjustPath(opts?.deviceLogin?.path);
  return () => {
    return GlobalState.dispatch((dispatch, getState) => {
      const {
        auth
      } = getState();
      if (auth.deviceAccessTokenRequest?.userCode) {
        dispatch(push(deviceLoginPath));
      } else {
        dispatch(push(loginPath));
      }
    });
  };
}
export function createOnAfterEnroll(opts) {
  const {
    adjustPath = defaultAdjustPath
  } = opts;
  const afterEnrollPath = adjustPath(opts.postLoginUrl || '/');
  return enrollRes => {
    GlobalState.dispatch({
      type: Constants.AUTH_LOGGING_IN
    });
    GlobalState.dispatch({
      type: Constants.AUTH_LOGGED_IN,
      profile: enrollRes.user,
      idToken: enrollRes.idToken
    });
    return GlobalState.dispatch(push(afterEnrollPath));
  };
}
export function defaultOnResetPassword(email) {
  return GlobalState.dispatch(Auth.forgotPassword(email, window.Molten?.getConfig?.('api/projectId') || process.env.PROJECT_ID, {
    uiName: window.Molten?.getConfig('uiName') || process.env.uiUserManagementName
  }));
}
export function defaultGetPasswordRequirements(projectId) {
  return GlobalState.dispatch((dispatch, getState, {
    api
  }) => {
    return api.project(projectId || 'shared').passwordRequirements().get();
  });
}
export function defaultGetPasswordlessLoginSettings(projectId) {
  return GlobalState.dispatch((dispatch, getState, {
    api
  }) => {
    return api.project(projectId || 'shared').passwordlessLoginSettings().get();
  });
}
export function defaultGetTwoFactorSettings(projectId) {
  return GlobalState.dispatch((dispatch, getState, {
    api
  }) => {
    return api.project(projectId || 'shared').twoFactorSettings().get();
  });
}
export function defaultRequest2FACode(method) {
  const state = GlobalState.get();
  const userId = state.auth.profile?.id;
  return GlobalState.dispatch(Users.generateTwoFactorCode(userId, method));
}